/**
 * The port an organization's interface engine traffic arrives on.
 *
 * An organization holds at most one production mapping and one test mapping, and a port belongs to
 * one mapping at a time. Both of those are unique indexes rather than anything this schema can
 * state, so flow-api turns the constraint violations into readable errors.
 */
import { OneOrManySchema, QueryBooleanSchema } from 'notable-common';
import { z } from 'zod';
/** The range a mapping may claim: the registered and dynamic ports, never a privileged one. */
export const FlowInterfaceEnginePortSchema = z.number().int().min(1024).max(65535);
/** Query parameters `GET /interface-engine-port-mappings` accepts. */
export const FlowInterfaceEnginePortMappingListQuerySchema = z.object({
    organizationId: OneOrManySchema(z.uuid())
        .describe('Return only mappings of this organization, or of any of these organizations.')
        .optional(),
    isTest: QueryBooleanSchema.describe('Return only test mappings, or with false only production ones.').optional(),
});
/** Path parameters the endpoints addressing one mapping accept. */
export const FlowInterfaceEnginePortMappingIdParamsSchema = z.object({
    id: z.uuid().describe('The mapping.'),
});
/**
 * Body `POST /interface-engine-port-mappings` accepts. Structural validation only: whether the port
 * is free, and whether the organization already has a mapping of this kind, are answered by the
 * database.
 */
export const FlowInterfaceEnginePortMappingCreateBodySchema = z.object({
    organizationId: z.uuid().describe('Organization whose traffic arrives on this port.'),
    isTest: z.boolean().describe('Whether this is the test mapping rather than the production one.'),
    port: FlowInterfaceEnginePortSchema.describe('The port.'),
});
/**
 * Body `PATCH /interface-engine-port-mappings/{id}` accepts.
 *
 * The port is all of it: an existing mapping cannot change hands or become the test one, since
 * either would leave whoever is already sending traffic to the old port pointing at another
 * organization.
 */
export const FlowInterfaceEnginePortMappingUpdateBodySchema = z.object({
    port: FlowInterfaceEnginePortSchema.describe('The port.').optional(),
});
//# sourceMappingURL=interfaceEnginePortMapping.js.map